import React, { useEffect, useMemo, useState } from "react"
import { Navigate } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import ExcelJS from "exceljs"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import DatePicker, { registerLocale } from "react-datepicker"
import ptBR from "date-fns/locale/pt-BR"
import "react-datepicker/dist/react-datepicker.css"
import api from "../../services/api"
import AuthService from "../../services/Auth.service"

registerLocale("pt-BR", ptBR)

const GRANULARIDADE_OPTIONS = [
  { value: "diario", label: "Linhas por dia" },
  { value: "pulso", label: "Por pulso" },
]

/** Converte Date local → yyyy-mm-dd (calendário, sem UTC). */
const toIsoDate = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ""
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

/** Exibe dd/mm/yyyy sem deslocar fuso (evita 01/02 virar 31/01). */
const formatDateBr = value => {
  if (!value) return "—"
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = String(value.getDate()).padStart(2, "0")
    const m = String(value.getMonth() + 1).padStart(2, "0")
    const y = value.getFullYear()
    return `${d}/${m}/${y}`
  }
  if (typeof value === "string") {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
    const br = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (br) return value
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })
}

const formatDateTimeBr = value => {
  if (!value) return "—"
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })
}

const formatNumber = (value, digits = 3) => {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    return (0).toLocaleString("pt-BR", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  }
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

const RelatorioConsumo = () => {
  const session = AuthService.getCurrentUser()
  const isAdmin = Number(session?.user?.DS_STATUS) === 10

  const [condominios, setCondominios] = useState([])
  const [selectedCondo, setSelectedCondo] = useState(null)
  const [medidores, setMedidores] = useState([])
  const [selectedMedidores, setSelectedMedidores] = useState([])
  const [dataIni, setDataIni] = useState(null)
  const [dataFim, setDataFim] = useState(null)
  const [excluirInvalidos, setExcluirInvalidos] = useState(true)
  const [granularidade, setGranularidade] = useState(GRANULARIDADE_OPTIONS[0])
  const [loading, setLoading] = useState(false)
  const [loadingMedidores, setLoadingMedidores] = useState(false)
  const [relatorio, setRelatorio] = useState(null)

  useEffect(() => {
    if (!isAdmin) return
    api
      .get("/empresa/")
      .then(res => {
        const options = (res.data || [])
          .map(e => ({
            value: e.OID_EMPRESA,
            label: e.NM_RAZAO_SOCIAL,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
        setCondominios(options)
      })
      .catch(() => {
        Swal.fire("Erro", "Não foi possível carregar os condomínios.", "error")
      })
  }, [isAdmin])

  useEffect(() => {
    setSelectedMedidores([])
    setMedidores([])
    setRelatorio(null)

    if (!selectedCondo?.value) return

    setLoadingMedidores(true)
    api
      .get(`/imovel/empresa/${selectedCondo.value}`)
      .then(res => {
        const options = (res.data || [])
          .map(elem => ({
            value: elem.OID_MEDIDOR,
            label: `${elem.NR_MEDIDOR} — ${elem.imovel || "Sem imóvel"}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
        setMedidores(options)
      })
      .catch(() => {
        setMedidores([])
        Swal.fire("Erro", "Não foi possível carregar os medidores.", "error")
      })
      .finally(() => setLoadingMedidores(false))
  }, [selectedCondo])

  const isPulso = relatorio?.opcoes?.granularidade === "pulso"

  const fileBaseName = useMemo(() => {
    if (!relatorio) return "relatorio-consumo"
    const condo = (relatorio.condominio?.NM_RAZAO_SOCIAL || "condominio")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase()
    const modo = relatorio.opcoes?.granularidade || "diario"
    return `consumo-${modo}-${condo}-${relatorio.periodo?.ini || ""}-${
      relatorio.periodo?.fim || ""
    }`
  }, [relatorio])

  const opcaoInvalidoLabel = relatorio?.opcoes?.excluir_invalidos
    ? "Excluindo inválidos"
    : "Incluindo inválidos"

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  const gerarRelatorio = async () => {
    if (!selectedCondo?.value) {
      Swal.fire("Atenção", "Selecione o condomínio.", "warning")
      return
    }
    if (!dataIni || !dataFim) {
      Swal.fire("Atenção", "Informe a data início e a data fim.", "warning")
      return
    }

    const iniIso = toIsoDate(dataIni)
    const fimIso = toIsoDate(dataFim)

    if (iniIso > fimIso) {
      Swal.fire(
        "Atenção",
        "A data fim não pode ser menor que a data início.",
        "warning",
      )
      return
    }

    setLoading(true)
    setRelatorio(null)
    try {
      const body = {
        OID_CONDOMINIO: selectedCondo.value,
        DT_FILTER_INI: iniIso,
        DT_FILTER_FIM: fimIso,
        EXCLUIR_INVALIDOS: excluirInvalidos,
        GRANULARIDADE: granularidade?.value || "diario",
      }

      if (selectedMedidores.length > 0) {
        body.OID_MEDIDORES = selectedMedidores.map(m => m.value)
      }

      const { data } = await api.post("/consumptions/relatorio", body)
      setRelatorio(data)
      if (!data?.itens?.length) {
        Swal.fire(
          "Sem dados",
          "Nenhuma leitura encontrada no período com os filtros atuais.",
          "info",
        )
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Não foi possível gerar o relatório."
      Swal.fire("Erro", Array.isArray(msg) ? msg[0] : msg, "error")
    } finally {
      setLoading(false)
    }
  }

  const baixarXlsx = async () => {
    if (!relatorio?.itens?.length) return

    const workbook = new ExcelJS.Workbook()
    workbook.creator = "Syskeeper"
    workbook.created = new Date()

    const sheet = workbook.addWorksheet("Consumo")
    if (isPulso) {
      sheet.columns = [
        { header: "Data/Hora", key: "dt", width: 20 },
        { header: "Bloco", key: "bloco", width: 10 },
        { header: "Imóvel", key: "imovel", width: 18 },
        { header: "Hidrômetro", key: "hidrometro", width: 18 },
        { header: "Nr. Pulso", key: "pulso", width: 12 },
        { header: "Leitura", key: "leitura", width: 14 },
        { header: "Consumo (L)", key: "litros", width: 14 },
        { header: "Consumo (m³)", key: "m3", width: 14 },
      ]
      relatorio.itens.forEach(item => {
        sheet.addRow({
          dt: formatDateTimeBr(item.DT_LEITURA),
          bloco: item.DS_BLOCO || "",
          imovel: item.DS_COMPLEMENTO || "",
          hidrometro: item.NR_HIDROMETRO || "",
          pulso: Number(item.NR_PULSO),
          leitura: Number(item.LEITURA_ATUAL),
          litros: Number(item.CONSUMO_LITROS),
          m3: Number(item.CONSUMO_M3),
        })
      })
    } else {
      sheet.columns = [
        { header: "Data", key: "dia", width: 12 },
        { header: "Bloco", key: "bloco", width: 10 },
        { header: "Imóvel", key: "imovel", width: 18 },
        { header: "Hidrômetro", key: "hidrometro", width: 18 },
        { header: "Leit. ini", key: "leitura_ini", width: 14 },
        { header: "Leit. fim", key: "leitura_fim", width: 14 },
        { header: "Qtd. leituras", key: "qtd", width: 12 },
        { header: "Consumo (L)", key: "litros", width: 14 },
        { header: "Consumo (m³)", key: "m3", width: 14 },
      ]
      relatorio.itens.forEach(item => {
        sheet.addRow({
          dia: formatDateBr(item.DT_DIA || item.DT_PRIMEIRA),
          bloco: item.DS_BLOCO || "",
          imovel: item.DS_COMPLEMENTO || "",
          hidrometro: item.NR_HIDROMETRO || "",
          leitura_ini: Number(item.LEITURA_INI),
          leitura_fim: Number(item.LEITURA_FIM),
          qtd: Number(item.QTD_LEITURAS || 0),
          litros: Number(item.CONSUMO_LITROS),
          m3: Number(item.CONSUMO_M3),
        })
      })
    }

    sheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF556EE6" },
      }
      cell.alignment = { vertical: "middle", horizontal: "center" }
    })

    sheet.addRow({})
    const totalRow = sheet.addRow({
      bloco: "",
      imovel: "TOTAL DO PERÍODO",
      hidrometro: `${relatorio.qtd_medidores} medidor(es)`,
      litros: Number(relatorio.total_litros),
      m3: Number(relatorio.total_m3),
    })
    totalRow.font = { bold: true }

    const meta = workbook.addWorksheet("Resumo")
    meta.addRow(["Condomínio", relatorio.condominio?.NM_RAZAO_SOCIAL || ""])
    meta.addRow(["Período início", formatDateBr(relatorio.periodo?.ini)])
    meta.addRow(["Período fim", formatDateBr(relatorio.periodo?.fim)])
    meta.addRow([
      "Granularidade",
      isPulso ? "Por pulso" : "Linhas por dia",
    ])
    meta.addRow(["Medidores", relatorio.qtd_medidores])
    meta.addRow(["Linhas", relatorio.qtd_linhas ?? relatorio.itens.length])
    meta.addRow([
      "Filtro medidores",
      Array.isArray(relatorio.opcoes?.medidores_filtro)
        ? relatorio.opcoes.medidores_filtro.join(", ")
        : "Todos",
    ])
    meta.addRow(["Pulsos inválidos", opcaoInvalidoLabel])
    meta.addRow(["Total (litros)", Number(relatorio.total_litros)])
    meta.addRow(["Total (m³)", Number(relatorio.total_m3)])
    meta.getColumn(1).width = 18
    meta.getColumn(2).width = 80

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${fileBaseName}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  }

  const baixarPdf = () => {
    if (!relatorio?.itens?.length) return

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })
    const pageWidth = doc.internal.pageSize.getWidth()

    doc.setFont("helvetica", "bold")
    doc.setFontSize(16)
    doc.setTextColor(85, 110, 230)
    doc.text("Syskeeper", 14, 16)

    doc.setFontSize(12)
    doc.setTextColor(50, 50, 50)
    doc.text("Relatório de Consumo", 14, 24)

    doc.setFont("helvetica", "normal")
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)
    doc.text(
      `Condomínio: ${relatorio.condominio?.NM_RAZAO_SOCIAL || "—"}`,
      14,
      31,
    )
    doc.text(
      `Período: ${formatDateBr(relatorio.periodo?.ini)} a ${formatDateBr(
        relatorio.periodo?.fim,
      )}  |  ${isPulso ? "Por pulso" : "Por dia"}  |  ${opcaoInvalidoLabel}`,
      14,
      37,
    )
    doc.text(
      `Medidores: ${relatorio.qtd_medidores}  |  Total: ${formatNumber(
        relatorio.total_litros,
        0,
      )} L  (${formatNumber(relatorio.total_m3)} m³)`,
      14,
      43,
    )

    const head = isPulso
      ? [
          [
            "Data/Hora",
            "Bloco",
            "Imóvel",
            "Hidrômetro",
            "Pulso",
            "Leitura",
            "Litros",
            "m³",
          ],
        ]
      : [
          [
            "Data",
            "Bloco",
            "Imóvel",
            "Hidrômetro",
            "Leit. ini",
            "Leit. fim",
            "Qtd",
            "Litros",
            "m³",
          ],
        ]

    const body = isPulso
      ? relatorio.itens.map(item => [
          formatDateTimeBr(item.DT_LEITURA),
          item.DS_BLOCO || "",
          item.DS_COMPLEMENTO || "",
          item.NR_HIDROMETRO || "",
          String(item.NR_PULSO ?? ""),
          String(item.LEITURA_ATUAL ?? ""),
          formatNumber(item.CONSUMO_LITROS, 0),
          formatNumber(item.CONSUMO_M3),
        ])
      : relatorio.itens.map(item => [
          formatDateBr(item.DT_DIA || item.DT_PRIMEIRA),
          item.DS_BLOCO || "",
          item.DS_COMPLEMENTO || "",
          item.NR_HIDROMETRO || "",
          String(item.LEITURA_INI ?? ""),
          String(item.LEITURA_FIM ?? ""),
          String(item.QTD_LEITURAS ?? ""),
          formatNumber(item.CONSUMO_LITROS, 0),
          formatNumber(item.CONSUMO_M3),
        ])

    autoTable(doc, {
      startY: 48,
      head,
      body,
      styles: { fontSize: 7.5, cellPadding: 1.3 },
      headStyles: {
        fillColor: [85, 110, 230],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      foot: [
        isPulso
          ? [
              "",
              "",
              "TOTAL",
              `${relatorio.qtd_medidores} medidor(es)`,
              "",
              "",
              formatNumber(relatorio.total_litros, 0),
              formatNumber(relatorio.total_m3),
            ]
          : [
              "",
              "",
              "TOTAL",
              `${relatorio.qtd_medidores} medidor(es)`,
              "",
              "",
              "",
              formatNumber(relatorio.total_litros, 0),
              formatNumber(relatorio.total_m3),
            ],
      ],
      footStyles: { fillColor: [232, 236, 245], fontStyle: "bold" },
      margin: { left: 14, right: 14 },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages()
        doc.setFontSize(8)
        doc.setTextColor(140, 140, 140)
        doc.text(
          `Gerado em ${new Date().toLocaleString("pt-BR")}  |  Syskeeper`,
          14,
          doc.internal.pageSize.getHeight() - 8,
        )
        doc.text(
          `Página ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`,
          pageWidth - 14,
          doc.internal.pageSize.getHeight() - 8,
          { align: "right" },
        )
      },
    })

    doc.save(`${fileBaseName}.pdf`)
  }

  const datePickerProps = {
    dateFormat: "dd/MM/yyyy",
    locale: "pt-BR",
    className: "form-control",
    placeholderText: "dd/mm/aaaa",
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: "select",
    popperPlacement: "bottom-start",
    portalId: "root-portal",
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <div className="mb-4">
                  <h4 className="card-title mb-1">Relatório de Consumo</h4>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    Total do período no topo. Escolha linhas por dia ou por
                    pulso.
                  </p>
                </div>

                <Row className="g-3 mb-2">
                  <Col md={6} lg={3}>
                    <label className="form-label">Condomínio</label>
                    <Select
                      options={condominios}
                      value={selectedCondo}
                      onChange={setSelectedCondo}
                      placeholder="Selecione..."
                      isClearable
                      isSearchable
                    />
                  </Col>
                  <Col md={6} lg={3}>
                    <label className="form-label">
                      Medidores{" "}
                      <span className="text-muted">(vazio = todos)</span>
                    </label>
                    <Select
                      options={medidores}
                      value={selectedMedidores}
                      onChange={opts => setSelectedMedidores(opts || [])}
                      placeholder={
                        loadingMedidores
                          ? "Carregando..."
                          : selectedCondo
                            ? "Todos os medidores"
                            : "Selecione o condomínio"
                      }
                      isMulti
                      isClearable
                      isSearchable
                      isDisabled={!selectedCondo || loadingMedidores}
                      closeMenuOnSelect={false}
                    />
                  </Col>
                  <Col md={3} lg={2}>
                    <label className="form-label">Data início</label>
                    <DatePicker
                      {...datePickerProps}
                      selected={dataIni}
                      onChange={setDataIni}
                      selectsStart
                      startDate={dataIni}
                      endDate={dataFim}
                      maxDate={dataFim || undefined}
                    />
                  </Col>
                  <Col md={3} lg={2}>
                    <label className="form-label">Data fim</label>
                    <DatePicker
                      {...datePickerProps}
                      selected={dataFim}
                      onChange={setDataFim}
                      selectsEnd
                      startDate={dataIni}
                      endDate={dataFim}
                      minDate={dataIni || undefined}
                    />
                  </Col>
                  <Col md={6} lg={2}>
                    <label className="form-label">Linhas</label>
                    <Select
                      options={GRANULARIDADE_OPTIONS}
                      value={granularidade}
                      onChange={opt =>
                        setGranularidade(opt || GRANULARIDADE_OPTIONS[0])
                      }
                      isSearchable={false}
                    />
                  </Col>
                </Row>

                <Row className="g-3 align-items-center mb-3">
                  <Col md={6} lg={5}>
                    <div className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="excluirInvalidos"
                        checked={excluirInvalidos}
                        onChange={() => setExcluirInvalidos(v => !v)}
                      />
                      <label
                        className="form-check-label ms-1"
                        htmlFor="excluirInvalidos"
                      >
                        Excluir pulsos inválidos
                        <span
                          className="d-block text-muted"
                          style={{ fontSize: 12, fontWeight: 400 }}
                        >
                          Remove vazios/NaN e zera rollbacks (recomendado)
                        </span>
                      </label>
                    </div>
                  </Col>
                  <Col
                    md={6}
                    lg={7}
                    className="d-flex gap-2 flex-wrap justify-content-lg-end"
                  >
                    <Button
                      color="primary"
                      onClick={gerarRelatorio}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Gerando...
                        </>
                      ) : (
                        "Gerar"
                      )}
                    </Button>
                    <Button
                      color="success"
                      outline
                      onClick={baixarXlsx}
                      disabled={!relatorio?.itens?.length || loading}
                    >
                      Excel
                    </Button>
                    <Button
                      color="danger"
                      outline
                      onClick={baixarPdf}
                      disabled={!relatorio?.itens?.length || loading}
                    >
                      PDF
                    </Button>
                  </Col>
                </Row>

                {relatorio ? (
                  <>
                    <div
                      className="d-flex flex-wrap gap-4 mb-3 p-3 rounded"
                      style={{
                        background: "#f8f9fb",
                        border: "1px solid #eef0f4",
                      }}
                    >
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Condomínio
                        </div>
                        <strong>
                          {relatorio.condominio?.NM_RAZAO_SOCIAL || "—"}
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Período
                        </div>
                        <strong>
                          {formatDateBr(relatorio.periodo?.ini)} —{" "}
                          {formatDateBr(relatorio.periodo?.fim)}
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Modo
                        </div>
                        <strong>{isPulso ? "Por pulso" : "Por dia"}</strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Medidores
                        </div>
                        <strong>{relatorio.qtd_medidores}</strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Linhas
                        </div>
                        <strong>
                          {relatorio.qtd_linhas ?? relatorio.itens?.length ?? 0}
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Total litros
                        </div>
                        <strong style={{ color: "#556ee6", fontSize: 18 }}>
                          {formatNumber(relatorio.total_litros, 0)} L
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Total m³
                        </div>
                        <strong style={{ color: "#556ee6", fontSize: 18 }}>
                          {formatNumber(relatorio.total_m3)} m³
                        </strong>
                      </div>
                    </div>

                    <div
                      className="table-responsive"
                      style={{ maxHeight: 520 }}
                    >
                      <Table className="table table-sm table-hover mb-0">
                        <thead
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "#fff",
                            zIndex: 1,
                          }}
                        >
                          {isPulso ? (
                            <tr>
                              <th>Data/Hora</th>
                              <th>Bloco</th>
                              <th>Imóvel</th>
                              <th>Hidrômetro</th>
                              <th className="text-end">Pulso</th>
                              <th className="text-end">Leitura</th>
                              <th className="text-end">Litros</th>
                              <th className="text-end">m³</th>
                            </tr>
                          ) : (
                            <tr>
                              <th>Data</th>
                              <th>Bloco</th>
                              <th>Imóvel</th>
                              <th>Hidrômetro</th>
                              <th className="text-end">Leit. ini</th>
                              <th className="text-end">Leit. fim</th>
                              <th className="text-end">Qtd</th>
                              <th className="text-end">Litros</th>
                              <th className="text-end">m³</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {isPulso
                            ? relatorio.itens.map((item, idx) => (
                                <tr
                                  key={`${item.OID_MEDIDOR}-${item.DT_LEITURA}-${idx}`}
                                >
                                  <td>{formatDateTimeBr(item.DT_LEITURA)}</td>
                                  <td>{item.DS_BLOCO || "—"}</td>
                                  <td>{item.DS_COMPLEMENTO || "—"}</td>
                                  <td>{item.NR_HIDROMETRO}</td>
                                  <td className="text-end">{item.NR_PULSO}</td>
                                  <td className="text-end">
                                    {item.LEITURA_ATUAL}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_LITROS, 0)}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_M3)}
                                  </td>
                                </tr>
                              ))
                            : relatorio.itens.map((item, idx) => (
                                <tr
                                  key={`${item.OID_MEDIDOR}-${item.DT_DIA}-${idx}`}
                                >
                                  <td>
                                    {formatDateBr(
                                      item.DT_DIA || item.DT_PRIMEIRA,
                                    )}
                                  </td>
                                  <td>{item.DS_BLOCO || "—"}</td>
                                  <td>{item.DS_COMPLEMENTO || "—"}</td>
                                  <td>{item.NR_HIDROMETRO}</td>
                                  <td className="text-end">
                                    {item.LEITURA_INI}
                                  </td>
                                  <td className="text-end">
                                    {item.LEITURA_FIM}
                                  </td>
                                  <td className="text-end">
                                    {item.QTD_LEITURAS}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_LITROS, 0)}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_M3)}
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                ) : (
                  !loading && (
                    <div
                      className="text-center text-muted py-5"
                      style={{
                        border: "1px dashed #dde1ea",
                        borderRadius: 8,
                      }}
                    >
                      Configure os filtros e clique em Gerar para ver o
                      relatório.
                    </div>
                  )
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default RelatorioConsumo
